/**
 * Environment prefix for the browser tab title.
 *
 * Several EZX apps sit open side by side, often the same app pointed at
 * different environments, and Chrome truncates a tab title to a few characters.
 * The prefix therefore goes FIRST — it has to survive the truncation that hides
 * the app name, which is the whole point of having it.
 *
 * Production is deliberately left unprefixed. An undecorated title means the
 * real thing; anything decorated is somewhere it is safe to experiment.
 *
 * Ported from admin-web with one difference: carrier-web has no Sentry, so
 * there is no `VITE_SENTRY_ENVIRONMENT` to fall back on. `VITE_APP_ENV` is
 * provisioned for both carrier-web environments in `ezx-infra`.
 */

/** Railway names its environments `preview-v2` / `production-v2`. */
const RAILWAY_V2_SUFFIX = /-v2$/;

export interface TitleEnv {
  /** True under `vite dev`, false in any built bundle. */
  readonly DEV?: boolean;
  /** Set to `${{RAILWAY_ENVIRONMENT_NAME}}` for every deploy. */
  readonly VITE_APP_ENV?: string;
}

/**
 * The prefix to prepend to the document title, including its trailing space,
 * or `""` when the title should be left alone.
 */
export function environmentTitlePrefix(env: TitleEnv): string {
  // The dev server is unambiguous, and it is the one environment nobody
  // configures — so it must not depend on a variable anyone has to remember.
  if (env.DEV) return "[Dev] ";

  const name = (env.VITE_APP_ENV ?? "")
    .trim()
    .replace(RAILWAY_V2_SUFFIX, "")
    .toLowerCase();

  if (!name || name === "production") return "";

  return `[${name.charAt(0).toUpperCase()}${name.slice(1)}] `;
}
